import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Link } from "react-router-dom";
import { GoLinkExternal } from "react-icons/go";
import { API_TOKEN, API_URL, NEST_BACKEND_API } from "../../../Config/secrets";
import { Globe } from "react-feather";

// ── Attribute pill list ──────────────────────────────────────────────────────
const PillList = ({ values = [], mainValue }) =>
  values?.length ? (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {values.map((v, i) => {
        const isMain = mainValue
          ? v?.toLowerCase() === mainValue?.toLowerCase()
          : i === 0;
        return (
          <span
            key={i}
            style={{
              padding: "2px 10px",
              borderRadius: "12px",
              backgroundColor: isMain ? "#fcedc0" : "#f5f5f5",
              color: isMain ? "#e8b009" : "#333",
              fontWeight: isMain ? 600 : 400,
              fontSize: "13px",
            }}
          >
            {v}
          </span>
        );
      })}
    </div>
  ) : (
    <span style={{ color: "#999", fontSize: "13px" }}>--</span>
  );

// ── Key / value row (Further Details style) ──────────────────────────────────
const AttrRow = ({ label, children }) => (
  <div className="d-flex align-items-start border-bottom py-2">
    <h6
      style={{ fontWeight: "bold", flex: "0 0 38%", margin: 0, paddingTop: "2px" }}
    >
      {label}
    </h6>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, count }) => (
  <div
    className="d-flex py-2 px-3 align-items-center justify-content-between mb-3"
    style={{ backgroundColor: "#000", borderRadius: "8px" }}
  >
    <h6 style={{ color: "#fff", margin: 0 }}>
      {title}
      {count != null && (
        <span
          style={{
            marginLeft: "8px",
            backgroundColor: "#444",
            borderRadius: "10px",
            padding: "0 8px",
            fontSize: "12px",
          }}
        >
          {count}
        </span>
      )}
    </h6>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const WorkingWithModal = ({ open, onClose, matchItem }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);


  const fetchWorkingWith = useCallback(
    async (pageNum, rowsPerPage) => {
      if (!matchItem?.id || !matchItem?.type) return;
      setLoading(true);
      try {
        const response = await axios.post(
          `${API_URL}/matches/get-matches`,
          {
            id: matchItem.id,
            entity_type: matchItem.type,
            tab: "matches",
            matched_by_ai: false,
            skip: (pageNum - 1) * rowsPerPage,
            take: rowsPerPage,
          },
          { headers: { Authorization: API_TOKEN } }
        );
        if (response?.data?.statusCode === 200) {
          setItems(response.data.data?.items || []);
          setTotal(response.data.data?.total || 0);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    },
    [matchItem?.id, matchItem?.type]
  );

  useEffect(() => {
    if (open && matchItem?.id) {
      setPage(1);
      setItems([]);
      setTotal(0);
      fetchWorkingWith(1, perPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matchItem?.id]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchWorkingWith(newPage, perPage);
  };

  const handleRowsPerPageChange = (newPerPage, currentPage) => {
    setPerPage(newPerPage);
    setPage(currentPage);
    fetchWorkingWith(currentPage, newPerPage);
  };

  const columns = [
    {
      name: "Name",
      cell: (row) => row.name || "--",
      width: "350px",
      sortable: false,
    },
    {
      name: "Country",
      cell: (row) => row.country?.toUpperCase() || "--",
      sortable: false,
    },
    {
      name: "Website",
      cell: (row) =>
        row.website ? <a href={row.website} target="_blank" rel="noopener noreferrer"><Globe size={16} /></a> : "--",
      sortable: false,
      center: true,
    },
    {
      name: "Profile",
      cell: (row) => (
        <Link
          to={`/profile/${row.id}/${row.type || matchItem?.type}`}
          target="_blank"
          title="Open profile"
        >
          <GoLinkExternal size={15} color="#555" />
        </Link>
      ),
      sortable: false,
      center: true,
    },
  ];

  if (!matchItem) return null;

  return (
    <Modal isOpen={open} toggle={onClose} centered style={{ maxWidth: "1400px" }}>
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center gap-2">
          {(matchItem.logo || matchItem.profile_img) && (
            <img
              src={matchItem.logo || matchItem.profile_img}
              alt={matchItem.name}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          )}
          <span>{matchItem.name}</span>
          <Link
            to={`/profile/${matchItem.id}/${matchItem?.type}`}
            target="_blank"
            style={{ color: "inherit" }}
          >
            <GoLinkExternal size={16} />
          </Link>
        </div>
      </ModalHeader>

      <ModalBody
        style={{ maxHeight: "78vh", overflowY: "auto", padding: "1.25rem 1.5rem" }}
      >
        {/* ── Profile Attributes ── */}
        <SectionHeader title="Profile Attributes" />

        <AttrRow label="Name">
          <h6 style={{ margin: 0, lineHeight: 1.5 }}>{matchItem.name || "--"}</h6>
        </AttrRow>

        <AttrRow label="Country">
          <h6 style={{ margin: 0, lineHeight: 1.5 }}>{matchItem.country || "--"}</h6>
        </AttrRow>

        <AttrRow label="Target Group">
          <PillList
            values={matchItem.target_group}
            mainValue={matchItem.main_target_group}
          />
        </AttrRow>

        <AttrRow label="Product Category">
          <PillList
            values={matchItem.product_category}
            mainValue={matchItem.main_category}
          />
        </AttrRow>

        <AttrRow label="Price">
          <PillList values={matchItem.price} />
        </AttrRow>

        <AttrRow label="Style">
          <PillList values={matchItem.style} mainValue={matchItem.main_style} />
        </AttrRow>

        {matchItem.price_point && (
          <AttrRow label="Price Point">
            <h6 style={{ margin: 0 }}>{matchItem.price_point}</h6>
          </AttrRow>
        )}

        {/* ── Working With table ── */}
        <div className="mt-4">
          <SectionHeader
            title="Already Working With"
            count={loading ? null : total}
          />

          <DataTable
            columns={columns}
            data={items}
            pagination
            paginationServer
            paginationTotalRows={total}
            paginationPerPage={perPage}
            paginationDefaultPage={page}
            progressPending={loading}
            progressComponent={
              <div className="d-flex justify-content-center py-5">
                <Spinner color="dark" />
              </div>
            }
            noDataComponent={
              <p className="text-muted text-center py-4">
                No "already working with" records found
              </p>
            }
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            striped
            dense
          />
        </div>
      </ModalBody>
    </Modal>
  );
};

export default WorkingWithModal;
